// Edição da aparência por peças (o editor de personagem): sorteio, validação do Look que vai
// para os ajustes e regra de nome único. Puro (sem DOM): testado em Node.
//
// O Look tem UM slot de acessório. Olhos (óculos), barba e acessório disputam esse slot: o
// editor mostra os três grupos, e escolher um deles tira o que estava no outro.

import { ACCESSORY_IDS, BODY_IDS, CLOTH_COLOR_COUNT, HAIR_COLOR_COUNT, HAIR_IDS, OUTFIT_IDS, SKIN_TONE_COUNT, type AccessoryId, type Look } from './look.js';
import type { PersonaSettings } from './settings.js';

/** Óculos: as peças de "olhos" do editor. */
export const OLHOS: readonly AccessoryId[] = ['oculos', 'oculos-escuros'];
/** Barba e bigode. */
export const BARBA: readonly AccessoryId[] = ['barba', 'bigode'];
/** Os demais acessórios (os que não são óculos nem barba). */
export const ACESSORIOS: readonly AccessoryId[] = ACCESSORY_IDS.filter((a) => !OLHOS.includes(a) && !BARBA.includes(a));

/** Tamanho máximo do nome do personagem (letras). */
export const NOME_MAX = 24;

const ate = (n: number, rand: () => number): number => Math.floor(rand() * n);
const pega = <T>(lista: readonly T[], rand: () => number): T => lista[ate(lista.length, rand)] as T;

/** Aparência sorteada: todas as peças; acessório em um de cada três, como no sorteio automático. */
export function sorteiaLook(rand: () => number = Math.random): Look {
  const look: Look = {
    body: pega(BODY_IDS, rand),
    skin: ate(SKIN_TONE_COUNT, rand),
    hair: pega(HAIR_IDS, rand),
    hairColor: ate(HAIR_COLOR_COUNT, rand),
    outfit: pega(OUTFIT_IDS, rand),
    shirt: ate(CLOTH_COLOR_COUNT, rand),
    pants: ate(CLOTH_COLOR_COUNT, rand),
    shoes: ate(CLOTH_COLOR_COUNT, rand),
  };
  if (rand() < 1 / 3) return { ...look, accessory: pega(ACCESSORY_IDS, rand), accColor: ate(CLOTH_COLOR_COUNT, rand) };
  return look;
}

/** Põe o acessório (ou tira, com undefined). A cor fica a que já estava, ou a 0. */
export function comAcessorio(look: Look, accessory: AccessoryId | undefined): Look {
  const semAcessorio: Look = { ...look };
  delete semAcessorio.accessory;
  delete semAcessorio.accColor;
  if (!accessory) return semAcessorio;
  return { ...semAcessorio, accessory, accColor: look.accColor ?? 0 };
}

/** Erros de uma aparência (vazio = válida). `onde` aparece na mensagem, ex.: 'personagem project:x'. */
export function lookErrors(look: unknown, onde: string): string[] {
  if (!look || typeof look !== 'object') return [`${onde}: aparência inválida`];
  const l = look as Record<string, unknown>;
  const erros: string[] = [];
  const ok = (lista: readonly unknown[], valor: unknown): boolean => lista.includes(valor);
  const cor = (valor: unknown, max: number): boolean => Number.isInteger(valor) && (valor as number) >= 0 && (valor as number) < max;
  if (!ok(BODY_IDS, l.body)) erros.push(`${onde}: corpo inválido`);
  if (!cor(l.skin, SKIN_TONE_COUNT)) erros.push(`${onde}: tom de pele inválido`);
  if (!ok(HAIR_IDS, l.hair)) erros.push(`${onde}: cabelo inválido`);
  if (!cor(l.hairColor, HAIR_COLOR_COUNT)) erros.push(`${onde}: cor do cabelo inválida`);
  if (!ok(OUTFIT_IDS, l.outfit)) erros.push(`${onde}: roupa inválida`);
  for (const campo of ['shirt', 'pants', 'shoes'] as const) {
    if (!cor(l[campo], CLOTH_COLOR_COUNT)) erros.push(`${onde}: cor de ${campo === 'shirt' ? 'roupa de cima' : campo === 'pants' ? 'roupa de baixo' : 'sapato'} inválida`);
  }
  if (l.accessory !== undefined && !ok(ACCESSORY_IDS, l.accessory)) erros.push(`${onde}: acessório inválido`);
  if (l.accColor !== undefined && !cor(l.accColor, CLOTH_COLOR_COUNT)) erros.push(`${onde}: cor do acessório inválida`);
  return erros;
}

/** Minúsculas, sem acento e sem espaço nas pontas: é assim que os nomes são comparados. */
export function chaveDeNome(nome: string): string {
  return nome.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();
}

/** Problema do nome (undefined = ok): vazio, longo demais ou já usado por outro. */
export function nomeProblema(nome: string, emUso: readonly string[]): 'vazio' | 'longo' | 'repetido' | undefined {
  const limpo = nome.trim();
  if (!limpo) return 'vazio';
  if (limpo.length > NOME_MAX) return 'longo';
  const chave = chaveDeNome(limpo);
  if (emUso.some((outro) => chaveDeNome(outro) === chave)) return 'repetido';
  return undefined;
}

/** Quem está no escritório e o que já foi salvo: o nome do personagem não pode repetir nenhum deles. */
export interface EscritorioNomes {
  agentes: ReadonlyArray<{ id: string; name: string }>;
  personas: Record<string, PersonaSettings>;
  projetos: readonly string[];
}

/**
 * Nomes que o personagem de `chave` não pode repetir: os outros agentes no escritório (menos o
 * próprio, `agenteId`), os outros personagens salvos (menos o de `chave`) e os outros projetos
 * (menos `projeto`).
 */
export function nomesEmUso(escritorio: EscritorioNomes, quem: { chave: string; agenteId?: string; projeto?: string }): string[] {
  return [
    ...escritorio.agentes.filter((a) => a.id !== quem.agenteId).map((a) => a.name),
    ...Object.entries(escritorio.personas)
      .filter(([chave]) => chave !== quem.chave)
      .flatMap(([, p]) => (p.name ? [p.name] : [])),
    ...escritorio.projetos.filter((p) => p !== quem.projeto),
  ];
}
